import React, { useEffect, useState } from "react";
import {
  collection, query, where, orderBy, limit, getDocs, getCountFromServer, Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useMembers } from "../hooks/useMembers";

interface Props {
  onTab: (id: string) => void;
}

interface SonTalep {
  id: string;
  ad: string;
  soyad: string;
  tarih: Timestamp | null;
  durum: string;
}

function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try { return t.toDate().toLocaleDateString("tr-TR"); } catch { return "—"; }
}

const DashboardPage: React.FC<Props> = ({ onTab }) => {
  const today = new Date();
  const { members, loading: membersLoading } = useMembers();

  const [uyeSayisi, setUyeSayisi] = useState<number | null>(null);
  const [bekleyenTalep, setBekleyenTalep] = useState<number | null>(null);
  const [bekleyenAvantaj, setBekleyenAvantaj] = useState<number | null>(null);
  const [smsSayisi, setSmsSayisi] = useState<number | null>(null);
  const [sonTalepler, setSonTalepler] = useState<SonTalep[]>([]);
  const [statLoading, setStatLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatLoading(true);
      try {
        // getCountFromServer — tüm dökümanları indirmeden sadece sayı çeker (verimli).
        const [uye, talep, avantaj, sms, sonSnap] = await Promise.all([
          getCountFromServer(collection(db, "members")),
          getCountFromServer(query(collection(db, "uyelikTalepleri"), where("durum", "==", "bekliyor"))),
          getCountFromServer(query(collection(db, "avantajBasvurulari"), where("durum", "==", "bekliyor"))),
          getCountFromServer(collection(db, "smsLogs")),
          getDocs(query(collection(db, "uyelikTalepleri"), orderBy("tarih", "desc"), limit(5))),
        ]);
        if (cancelled) return;
        setUyeSayisi(uye.data().count);
        setBekleyenTalep(talep.data().count);
        setBekleyenAvantaj(avantaj.data().count);
        setSmsSayisi(sms.data().count);
        setSonTalepler(sonSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as SonTalep[]);
      } catch (e) {
        console.error("Dashboard sayıları okunamadı:", e);
        if (!cancelled) {
          setUyeSayisi(0); setBekleyenTalep(0); setBekleyenAvantaj(0); setSmsSayisi(0);
        }
      } finally {
        if (!cancelled) setStatLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Doğum günleri — useMembers (birthDate string → dogumAy/dogumGun parse edilmiş)
  const todayBirthdays = members.filter(
    (m) => m.dogumAy === today.getMonth() + 1 && m.dogumGun === today.getDate()
  );
  const thisMonthBirthdays = members.filter((m) => m.dogumAy === today.getMonth() + 1);

  // Yükleniyorsa "…", yoksa sayı.
  const show = (v: number | null) => (statLoading || v === null ? "…" : v);
  const bekleyenToplam = (bekleyenTalep ?? 0) + (bekleyenAvantaj ?? 0);

  const stats = [
    { icon: "fa-users", label: "Toplam Üye", value: show(uyeSayisi), color: "#0F4C81", tab: "members" },
    { icon: "fa-file-lines", label: "Bekleyen Başvuru", value: statLoading ? "…" : bekleyenToplam, color: "#8B1428", tab: "applications" },
    { icon: "fa-cake-candles", label: "Bu Ay Doğum Günü", value: membersLoading ? "…" : thisMonthBirthdays.length, color: "#D97706", tab: "birthdays" },
    { icon: "fa-comment-sms", label: "Gönderilen SMS", value: show(smsSayisi), color: "#059669", tab: "sms-log" },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Genel Bakış</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          {today.toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* İstatistik kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <button key={s.label} onClick={() => onTab(s.tab)}
            className="bg-white rounded-2xl border border-gray-200 p-5 text-left hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                <i className={`fa-solid ${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <i className="fa-solid fa-arrow-right text-gray-300 group-hover:text-gray-500 text-xs transition-colors" />
            </div>
            <div className="text-2xl font-extrabold text-gray-800">{s.value}</div>
            <div className="text-sm text-gray-500 mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Bugünün doğum günleri */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-700 flex items-center gap-2">
              <i className="fa-solid fa-cake-candles text-pink-400" /> Bugün Doğum Günü
            </h3>
            <button onClick={() => onTab("birthdays")} className="text-xs text-[#8B1428] font-semibold hover:underline">
              Tümü <i className="fa-solid fa-arrow-right text-[10px]" />
            </button>
          </div>
          {membersLoading ? (
            <p className="text-gray-400 text-sm">Yükleniyor...</p>
          ) : todayBirthdays.length === 0 ? (
            <p className="text-gray-400 text-sm">Bugün doğum günü olan üye yok.</p>
          ) : (
            <div className="space-y-3">
              {todayBirthdays.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3 bg-pink-50 rounded-xl">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
                    {m.adSoyad.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{m.adSoyad}</div>
                    <div className="text-xs text-gray-500">{m.telefon || "Telefon yok"}</div>
                  </div>
                  <i className="fa-solid fa-cake-candles text-pink-400 ml-auto" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Son üyelik talepleri */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-700 flex items-center gap-2">
              <i className="fa-solid fa-file-lines text-[#8B1428]" /> Son Üyelik Talepleri
            </h3>
            <button onClick={() => onTab("applications")} className="text-xs text-[#8B1428] font-semibold hover:underline">
              Tümü <i className="fa-solid fa-arrow-right text-[10px]" />
            </button>
          </div>
          {statLoading ? (
            <p className="text-gray-400 text-sm">Yükleniyor...</p>
          ) : sonTalepler.length === 0 ? (
            <p className="text-gray-400 text-sm">Henüz talep yok.</p>
          ) : (
            <div className="space-y-3">
              {sonTalepler.map((a) => (
                <div key={a.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-xs font-bold flex-shrink-0">
                    {(a.ad?.[0] ?? "")}{(a.soyad?.[0] ?? "")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 text-sm truncate">{a.ad} {a.soyad}</div>
                    <div className="text-xs text-gray-400">{formatTarih(a.tarih)}</div>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0
                    ${a.durum === "bekliyor" ? "bg-yellow-100 text-yellow-700"
                      : a.durum === "onaylandi" ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-600"}`}>
                    {a.durum}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
